import {
  delegateActionCard,
  fetchDelegationTeammates,
  fetchReceivedDelegations,
  fetchSentDelegations,
  useSDK,
  type ActionCard,
  type DelegateCardInput,
} from '@littlebird/sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const TEAMMATES_STALE_MS = 5 * 60_000;

const delegationKeys = {
  teammates: ['action-card-delegations', 'teammates'] as const,
  sent: ['action-card-delegations', 'sent'] as const,
  received: ['action-card-delegations', 'received'] as const,
};

export const useDelegationTeammatesQuery = () => {
  const sdk = useSDK();
  return useQuery({
    queryKey: delegationKeys.teammates,
    queryFn: () => fetchDelegationTeammates(sdk),
    staleTime: TEAMMATES_STALE_MS,
  });
};

export const useSentDelegationsQuery = () => {
  const sdk = useSDK();
  return useQuery({
    queryKey: delegationKeys.sent,
    queryFn: () => fetchSentDelegations(sdk),
  });
};

// Stale at once, so each newly mounted card row refetches and a card that
// just arrived from a teammate is labelled
export const useReceivedDelegationsQuery = (enabled: boolean) => {
  const sdk = useSDK();
  return useQuery({
    queryKey: delegationKeys.received,
    queryFn: () => fetchReceivedDelegations(sdk),
    enabled,
  });
};

export const useDelegateCardMutation = () => {
  const sdk = useSDK();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: DelegateCardInput) => delegateActionCard(sdk, input),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: delegationKeys.sent }),
  });
};

export const useReceivedDelegationFor = (
  card: Pick<ActionCard, 'id' | 'origin' | 'chatId'>
) => {
  const mayBeDelegated = card.origin === 'user' && !card.chatId;
  const received = useReceivedDelegationsQuery(mayBeDelegated).data?.find(
    (delegation) => delegation.recipientCardId === card.id
  );
  return mayBeDelegated ? received : undefined;
};
